import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Calendar,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import TimePicker from "@/components/TimePicker";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function RequestsModule({ businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Reject modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectType, setRejectType] = useState(null); // "request" or "change"
  const [selectedItem, setSelectedItem] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processingAction, setProcessingAction] = useState(false);
  
  // Approve modal with details
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveRequest, setApproveRequest] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState("");
  const [selectedTrainer, setSelectedTrainer] = useState("");
  const [endTime, setEndTime] = useState("");

  useEffect(() => {
    if (businessId) {
      fetchData();
      fetchRoomsAndTrainers();
    }
  }, [businessId]);
  
  const fetchRoomsAndTrainers = async () => {
    try {
      const [roomsRes, trainersRes] = await Promise.all([
        fetch(`${API}/businesses/${businessId}/rooms`, { credentials: "include" }),
        fetch(`${API}/businesses/${businessId}/trainers`, { credentials: "include" }),
      ]);
      if (roomsRes.ok) setRooms(await roomsRes.json());
      if (trainersRes.ok) setTrainers(await trainersRes.json());
    } catch (error) {
      console.error("Error fetching rooms/trainers:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestsRes, changesRes] = await Promise.all([
        // Use new tab-based endpoint for requests (pending_business_approval)
        fetch(`${API}/reservations/by-tab/${businessId}?tab=requests&viewer=business`, { credentials: "include" }),
        fetch(`${API}/reservation-changes/pending?business_id=${businessId}`, { credentials: "include" }),
      ]);

      if (requestsRes.ok) setPendingRequests(await requestsRes.json());
      if (changesRes.ok) setPendingChanges(await changesRes.json());
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const openApproveModal = (request) => {
    setApproveRequest(request);
    setSelectedRoom(request.room_id || "none");
    setSelectedTrainer(request.trainer_id || "none");
    setEndTime(request.end_time || "");
    setShowApproveModal(true);
  };

  const handleApproveRequest = async (requestId) => {
    if (processingAction) return;
    setProcessingAction(true);
    
    try {
      // For the new system, requests are in reservations collection
      // Use business-approve endpoint
      const response = await fetch(`${API}/reservations/${requestId}/business-approve`, {
        method: "PUT",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t("admin:toast_talepOnaylandiRezervasyonOlusturuldu"));
        setShowApproveModal(false);
        setApproveRequest(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("admin:approveFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedItem || processingAction) return;
    setProcessingAction(true);

    // For requests (from reservations collection), use business-reject endpoint
    // For changes (from reservation_changes), use the old endpoint
    const endpoint = rejectType === "request" 
      ? `${API}/reservations/${selectedItem.reservation_id}/business-reject`
      : `${API}/reservation-changes/${selectedItem.change_id}/reject`;

    try {
      const response = await fetch(endpoint, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: rejectReason }),
      });

      if (response.ok) {
        toast.success(t('common:requestRejected'));
        setShowRejectModal(false);
        setSelectedItem(null);
        setRejectReason("");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:operationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleApproveChange = async (changeId) => {
    if (processingAction) return;
    setProcessingAction(true);
    
    try {
      const response = await fetch(`${API}/reservation-changes/${changeId}/approve`, {
        method: "PUT",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t("admin:toast_degisiklikOnaylandi"));
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("admin:approveFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setProcessingAction(false);
    }
  };

  const openRejectModal = (item, type) => {
    setSelectedItem(item);
    setRejectType(type);
    setRejectReason("");
    setShowRejectModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  const totalPending = pendingRequests.length + pendingChanges.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-xl text-navy">
          Talepler
          {totalPending > 0 && (
            <Badge className="ml-2 bg-orange-100 text-orange-700">{totalPending}</Badge>
          )}
        </h2>
        <Button variant="outline" size="sm" onClick={fetchData} className="rounded-xl">
          <RefreshCw className="w-4 h-4 mr-2" />
          Yenile
        </Button>
      </div>

      {/* Reservation Requests */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Rezervasyon Talepleri
            {pendingRequests.length > 0 && (
              <Badge variant="destructive">{pendingRequests.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingRequests.length > 0 ? (
            <div className="space-y-3">
              {pendingRequests.map((req) => (
                <div 
                  key={req.reservation_id || req.request_id}
                  className="p-4 bg-orange-50 border border-orange-200 rounded-xl"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-navy" />
                        <span className="font-semibold text-navy">{req.member_name}</span>
                        <Badge variant="secondary" className="text-xs">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Bekliyor
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <span className="text-grey">Tarih:</span>
                          <p className="font-medium">
                            {req.date ? format(new Date(req.date), "dd/MM/yyyy", { locale: getDateLocale() }) : "-"}
                          </p>
                        </div>
                        <div>
                          <span className="text-grey">Saat:</span>
                          <p className="font-medium">{req.start_time} - {req.end_time || ""}</p>
                        </div>
                        <div>
                          <span className="text-grey">Branş:</span>
                          <p className="font-medium">{getLocalizedBranch(req.branch) || "-"}</p>
                        </div>
                        <div>
                          <span className="text-grey">Antrenör:</span>
                          <p className="font-medium">{req.trainer_name || "-"}</p>
                        </div>
                      </div>
                      
                      {req.notes && (
                        <p className="text-sm text-grey mt-2 italic">&ldquo;{req.notes}&rdquo;</p>
                      )}
                      
                      <p className="text-xs text-grey mt-2">
                        Talep: {req.created_at ? format(new Date(req.created_at), "dd/MM HH:mm", { locale: getDateLocale() }) : ""}
                      </p>
                    </div>
                    
                    <div className="flex gap-2 pt-2 lg:pt-0 border-t lg:border-t-0 border-orange-200">
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700 rounded-xl flex-1 lg:flex-none"
                        onClick={() => openApproveModal(req)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Onayla
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        className="rounded-xl flex-1 lg:flex-none"
                        onClick={() => openRejectModal(req, "request")}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reddet
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-grey">
              <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Bekleyen rezervasyon talebi yok</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Requests */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Değişiklik Talepleri
            {pendingChanges.length > 0 && (
              <Badge variant="destructive">{pendingChanges.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingChanges.length > 0 ? (
            <div className="space-y-3">
              {pendingChanges.map((change) => (
                <div 
                  key={change.change_id}
                  className="p-4 bg-blue-50 border border-blue-200 rounded-xl"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-navy" />
                        <span className="font-semibold text-navy">{change.member_name}</span>
                        <Badge className="bg-blue-100 text-blue-700 text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          Değişiklik
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div className="p-2 bg-white rounded-lg">
                          <p className="text-xs text-grey mb-1">Mevcut:</p>
                          <p className="font-medium text-red-600">
                            {change.original_date} • {change.original_start_time}
                          </p>
                        </div>
                        <div className="p-2 bg-white rounded-lg">
                          <p className="text-xs text-grey mb-1">İstenen:</p>
                          <p className="font-medium text-green-600">
                            {change.new_date} • {change.new_start_time}
                          </p>
                        </div>
                      </div>
                      
                      {change.reason && (
                        <p className="text-sm text-grey mt-2 italic">Neden: &ldquo;{change.reason}&rdquo;</p>
                      )}
                      
                      <p className="text-xs text-grey mt-2">
                        Talep: {change.created_at ? format(new Date(change.created_at), "dd/MM HH:mm", { locale: getDateLocale() }) : ""}
                      </p>
                    </div>
                    
                    <div className="flex gap-2 pt-2 lg:pt-0 border-t lg:border-t-0 border-blue-200">
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700 rounded-xl flex-1 lg:flex-none"
                        onClick={() => handleApproveChange(change.change_id)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Onayla
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        className="rounded-xl flex-1 lg:flex-none"
                        onClick={() => openRejectModal(change, "change")}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reddet
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-grey">
              <RefreshCw className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>{t("reservations:noPendingChangeRequests")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Talebi Reddet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-red-50 rounded-xl">
              <p className="text-sm text-red-700">
                {rejectType === "request" 
                  ? `${selectedItem?.member_name} adlı üyenin rezervasyon talebini reddetmek üzeresiniz.`
                  : `${selectedItem?.member_name} adlı üyenin değişiklik talebini reddetmek üzeresiniz.`
                }
              </p>
            </div>
            
            <div>
              <Label>Red Nedeni</Label>
              <Textarea
                placeholder={t("admin:ph_reddetmeNedeniniziYazin")}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="rounded-xl"
              />
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1 rounded-xl"
                onClick={() => setShowRejectModal(false)}
                disabled={processingAction}
              >
                İptal
              </Button>
              <Button 
                variant="destructive"
                className="flex-1 rounded-xl"
                onClick={handleRejectRequest}
                disabled={processingAction}
              >
                {processingAction ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Reddediliyor...
                  </>
                ) : (
                  t('admin:reject')
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Approve Modal with Details */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              Rezervasyon Onayı
            </DialogTitle>
          </DialogHeader>
          
          {approveRequest && (
            <div className="space-y-4">
              {/* Request Summary */}
              <div className="bg-slate-50 p-4 rounded-xl">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-grey">Üye:</span>
                    <p className="font-semibold">{approveRequest.member_name}</p>
                  </div>
                  <div>
                    <span className="text-grey">Tarih:</span>
                    <p className="font-semibold">
                      {approveRequest.date ? format(new Date(approveRequest.date), "dd/MM/yyyy", { locale: getDateLocale() }) : "-"}
                    </p>
                  </div>
                  <div>
                    <span className="text-grey">Saat:</span>
                    <p className="font-semibold">{approveRequest.start_time}</p>
                  </div>
                  <div>
                    <span className="text-grey">Branş:</span>
                    <p className="font-semibold">{getLocalizedBranch(approveRequest.branch) || "-"}</p>
                  </div>
                </div>
              </div>
              
              {/* Editable Fields */}
              <div className="space-y-3">
                <div>
                  <Label>{t("common:odaSecimi")}</Label>
                  <Select value={selectedRoom} onValueChange={setSelectedRoom}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t("common:ph_odaSecin")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Oda Yok</SelectItem>
                      {rooms.filter(r => r.is_active).map((room) => (
                        <SelectItem key={room.room_id} value={room.room_id}>
                          {room.name} (Kapasite: {room.capacity})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>{t('common:trainer')}</Label>
                  <Select value={selectedTrainer} onValueChange={setSelectedTrainer}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t("common:selectTrainer")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('trainers:noTrainer')}</SelectItem>
                      {trainers.filter(t => t.is_active !== false).map((trainer) => (
                        <SelectItem key={trainer.trainer_id} value={trainer.trainer_id}>
                          {trainer.name} {trainer.surname}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>{t("common:bitisSaati")}</Label>
                  <TimePicker
                    value={endTime}
                    onChange={setEndTime}
                    minHour={8}
                    maxHour={22}
                    minuteStep={5}
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1 rounded-xl"
                  onClick={() => setShowApproveModal(false)}
                  disabled={processingAction}
                >
                  İptal
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
                  onClick={() => handleApproveRequest(approveRequest.reservation_id || approveRequest.request_id)}
                  disabled={processingAction}
                >
                  {processingAction ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Onaylanıyor...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Onayla ve Oluştur
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
